import type { OverviewCompetitorChartRow } from "@nexus/contracts";
import { formatTopicTitle } from "../components/OverviewChart/chartPrimitives.js";
import { formatCompAssetPct, scaleCompDaysAssets } from "./compTopicDetailMetrics.js";
import { formatCompactNumber } from "./formatCompactNumber.js";
import { textCell } from "./csv-cells.js";

type Bucket = OverviewCompetitorChartRow["bucket"];

/**
 * Column names of the Retool `overview_mix_chart_export` file this replaces. Client
 * reporting parses them by name, so they stay fixed English rather than translated.
 */
const COLUMNS = [
  "Topic Title",
  "Topic Description",
  "Days",
  "Assets",
  "Feature",
  "Sub-feature",
  "Time Frames",
  "Ad Types",
  "Asset %",
  "Assets_formatted",
  "Days/Assets",
  "Adge Score",
  "datasetName",
];

/** Retool joined the Feature array with a bare comma; the other set columns follow it. */
const LIST_SEPARATOR = ",";

const TIME_FRAME_SEPARATOR = " & ";

export interface CompetitorCsvSeries {
  key: Bucket;
  label: string;
}

/**
 * Asset % only: `toPrecision` strips IEEE artifacts like
 * `0.29 * 100 → 28.999999999999996` without rounding to the tooltip's integer percent.
 */
function percentCell(value: number): string {
  return String(Number(value.toPrecision(12)));
}

/** Unformatted number so spreadsheets can compute on it; empty cell for no-data. */
function numericCell(value: number | null | undefined): string {
  return typeof value === "number" ? String(value) : "";
}

function listCell(values: string[] | undefined, separator = LIST_SEPARATOR): string {
  return textCell((values ?? []).join(separator));
}

/** `120/1.5k (8%)` — the topic's assets over its entity's total, as the Retool file showed. */
function assetsFormattedCell(row: OverviewCompetitorChartRow): string {
  if (row.bucketTotalAssets == null) return "";
  return textCell(
    `${formatCompactNumber(row.Assets)}/${formatCompactNumber(row.bucketTotalAssets)} (${formatCompAssetPct(row.assetPct)})`,
  );
}

function toCsvRow(row: OverviewCompetitorChartRow, datasetName: string): string[] {
  return [
    textCell(formatTopicTitle(row.Topic_Title)),
    textCell(row.Topic_Description ?? ""),
    numericCell(row.Days),
    numericCell(row.Assets),
    listCell(row.features),
    listCell(row.subFeatures),
    listCell(row.timeFrames, TIME_FRAME_SEPARATOR),
    listCell(row.adTypes),
    percentCell(row.assetPct * 100),
    assetsFormattedCell(row),
    numericCell(scaleCompDaysAssets(row.daysAssets)),
    numericCell(row.adgeScore),
    textCell(datasetName),
  ];
}

/**
 * Serialize the Competitors Overview as the Retool export did: one row per plotted
 * topic × entity, topics in chart order and entities in legend order. An entity with
 * no row for a topic gets no line, so blank cells always mean "no value", never
 * "no data". Days/Assets is the plotted value, matching the chart dot and the drawer.
 */
export function buildCompetitorChartCsv(
  topics: string[],
  chartRows: OverviewCompetitorChartRow[],
  series: CompetitorCsvSeries[],
): string {
  const rowsByBucket = new Map<Bucket, Map<string, OverviewCompetitorChartRow>>();
  for (const row of chartRows) {
    const byTopic = rowsByBucket.get(row.bucket) ?? new Map<string, OverviewCompetitorChartRow>();
    byTopic.set(row.Topic_Title, row);
    rowsByBucket.set(row.bucket, byTopic);
  }

  const lines = topics.flatMap((topic) =>
    series.flatMap((s) => {
      const row = rowsByBucket.get(s.key)?.get(topic);
      return row ? [toCsvRow(row, s.label)] : [];
    }),
  );

  return [COLUMNS.map(textCell), ...lines].map((line) => line.join(",")).join("\n");
}

/** `competitors-overview-2026-09-24.csv` — dated so repeat exports do not collide. */
export function competitorChartCsvFilename(now: Date = new Date()): string {
  return `competitors-overview-${now.toISOString().slice(0, 10)}.csv`;
}
